import { Component, type ErrorInfo, type ReactNode } from "react";
import { createLogger } from "@core/utils";

const log = createLogger("core:error-boundary");

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Optional custom fallback; falls back to the default message otherwise. */
  fallback?: ReactNode;
  /** Label to identify where the boundary sits in logs (e.g. "viewer"). */
  label?: string;
};

type ErrorBoundaryState = { error: Error | null };

/**
 * App-wide React error boundary. The codebase previously had none, so any
 * uncaught render/commit/unmount error tore down the whole tree → blank screen.
 * This catches those, logs the error + component stack (so we can trace the
 * root cause), and shows a recoverable fallback instead of a blank page.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error(
      `[${this.props.label ?? "app"}] uncaught error: ${error.message}`,
      error,
      "\ncomponent stack:",
      info.componentStack,
    );
  }

  private handleReset = (): void => this.setState({ error: null });

  private handleReload = (): void => window.location.reload();

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-3 bg-white px-6 text-center">
        <p className="text-sm font-semibold text-neutral-800">
          Something went wrong
        </p>
        <p className="max-w-md text-xs text-neutral-500">
          {error.message || "An unexpected error occurred."}
        </p>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={this.handleReset}
            className="h-8 rounded-lg border border-neutral-200 px-3 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            className="h-8 rounded-lg bg-primary-600 px-3 text-[12px] font-medium text-white transition-colors hover:bg-primary-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
